import { ChallengeId, ChallengeSessionId, ChallengeStepId } from 'src/models/Challenge';
import { BackLink, Loader, WidgetLayout } from 'src/components';
import styled from 'styled-components';

import {
  usePracticePlan,
  useToggleStepCompleted,
  useReturnLink,
  useMarkVisited,
} from './hooks';
import StepList from './StepList';
import PracticePlanDetails from './PracticePlanDetails';
import PracticePlanSession from './PracticePlanSession';
import { useScrollToTop } from 'src/hooks';
import Comments from 'src/features/Comments';
import { useParams } from 'react-router-dom';
import { Navigate } from 'react-router-dom-v5-compat';
import { useQueryClient } from 'react-query';

const LoaderArea = styled.div`
  height: 100%;
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PracticePlan = () => {
  const { id, stepId } = useParams() as { id: ChallengeId, stepId?: ChallengeStepId };
  const { data: practicePlan, isLoading } = usePracticePlan(id);
  useScrollToTop([id, stepId]);
  useMarkVisited(id, stepId);
  const returnLink = useReturnLink();
  const step = practicePlan?.steps
    .flatMap(group => group.steps)
    .find(step => step.id === stepId);
  const sessionId = step?.session?.id;
  const isStepCompleted = !!step?.isCompleted;
  
  const commentsId = { challengeId: id, challengeStepId: step?.id, challengeSessionId: sessionId };

  const toggleStepCompleted = useToggleStepCompleted();

  const nextStep = practicePlan?.steps
    .flatMap(group => group.steps)
    .find(() => true);

  const steps = practicePlan?.steps
    .flatMap(group => group.steps) ?? [];
  const totalCount = steps.length;
  const completedCount = steps.filter(s => s.isCompleted).length;
  const progress = completedCount / totalCount || 0;

  return (
    <WidgetLayout>
      <WidgetLayout.MainContentArea>
        {(isLoading || !practicePlan) && (
          <LoaderArea>
            <Loader />
          </LoaderArea>
        )}
        {(!isLoading && !!practicePlan) && (
          <>
            {
              sessionId
                ? <BackLink label="Szczegóły planu praktyki" to={`/practice-plans/${id}`} />
                : <BackLink label={returnLink.label} to={returnLink.to} />
            }
            {
              sessionId && stepId
                ? (
                  <PracticePlanSession
                    id={sessionId}
                    isCompleted={isStepCompleted}
                    onToggleCompleted={() => toggleStepCompleted(id, stepId, !isStepCompleted)}
                  />
                )
                : (
                  <PracticePlanDetails {...practicePlan} />
                )
            }
          </>
        )}
      </WidgetLayout.MainContentArea>

      {(!isLoading && !!practicePlan && !stepId) && (
        <WidgetLayout.SecondaryContentArea>
          <Comments id={commentsId} />
        </WidgetLayout.SecondaryContentArea>
      )}

      {(!isLoading && !!practicePlan && sessionId && stepId) && (
        <WidgetLayout.SecondaryContentArea>
          <Comments id={commentsId} />
        </WidgetLayout.SecondaryContentArea>
      )}

      <WidgetLayout.WidgetArea>
        { practicePlan && (
          <StepList
            groups={practicePlan.steps}
            composeStepUrl={subpath => `/practice-plans/${id}/${subpath}`}
            progress={progress}
            currentStep={stepId}
            nextStep={nextStep}
            coach={practicePlan.coach}
            equipment={practicePlan.equipment}
            duration={practicePlan.duration ?? undefined}
            difficulty={practicePlan.difficulty}
            showBackLink={sessionId && !!stepId}
            practicePlanUrl={`/practice-plans/${id}`}
          />
        )}
      </WidgetLayout.WidgetArea>
    </WidgetLayout>
  );
};

export function PracticePlanBySession() {
  const { challengeSessionId } = useParams<{challengeSessionId: ChallengeSessionId}>();
  const queryClient = useQueryClient();
  const { data } = usePracticePlan(undefined, challengeSessionId);
  if (data != null) {
    queryClient.setQueryData(['practicePlans', data.id], data);
    const stepId = data.steps.flatMap(s => s.steps).find(s => s.session!.id === challengeSessionId)?.id;
    return (
      <Navigate to={`/practice-plans/${data.id}/${stepId}`} replace />
    );
  }
  return (
    <div>Loading...</div>
  );
}

export default PracticePlan;
